import React, { useEffect, useState } from "react";
import { Box, Button, Grid, IconButton, InputLabel, MenuItem, OutlinedInput, Paper, Select, SpeedDial, SpeedDialIcon, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DefaultDrawer from "../../components/default-drawer";
import { DefaultDialogProps } from "../../components/default-dialog";
import { useBaseLayoutController } from "../../base-layout/context/base-layout-controller";
import { getDataGridLocaleText, dataGridDefaultSx, dataGridDefaultProps } from "../../../config/datagrid-config";

export interface LookupFieldOption {
    id: number | string;
    name: string;
}

export interface LookupField {
    key: string;
    label: string;
    type?: 'text' | 'number' | 'select';
    required?: boolean;
    options?: LookupFieldOption[];
    maxLength?: number;
    // Nome do campo como ele vem no DTO de listagem, quando é diferente do nome
    // usado no Add/Update (ex.: Órgão lista como "name" mas salva como "nomOrgao").
    readKey?: string;
}

interface LookupManagerProps {
    title: string;
    fields: LookupField[];
    listFn: () => Promise<any[]>;
    addUpdateFn: (data: any) => Promise<any>;
    onSaved?: () => void;
    // 'embedded' = painel compacto dentro do formulário da tabela-mãe (ex.: dentro de Licitação)
    // 'standalone' = tela própria de gerenciamento, mesmo formato das telas antigas (ex.: Estrutura Governamental)
    variant?: 'embedded' | 'standalone';
}

// Tela genérica de "cadastro auxiliar": lista + formulário de adicionar/editar,
// reaproveitada por todos os lookups simples (Órgão, Fornecedor, Modalidade, Fase etc.)
export const LookupManager = ({ title, fields, listFn, addUpdateFn, onSaved, variant = 'embedded' }: LookupManagerProps) => {
    const { showMessage, showBackDrop, showDialog } = useBaseLayoutController();
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddEdit, setShowAddEdit] = useState(false);
    const [draft, setDraft] = useState<any>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const loadData = async () => {
        try {
            setLoading(true);
            const result = await listFn();
            // Normaliza o valor de leitura (readKey) para a mesma chave usada no
            // Add/Update, para a tabela e o formulário de edição usarem uma só chave.
            const normalized = (result || []).map((row: any) => {
                const mapped = { ...row };
                fields.forEach((f) => {
                    if (f.readKey && mapped[f.key] === undefined) {
                        mapped[f.key] = row[f.readKey];
                    }
                });
                return mapped;
            });
            setItems(normalized);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || `Erro ao carregar ${title}` });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        (async () => { await loadData(); })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openForm = (row?: any) => {
        setDraft(row ? { ...row } : {});
        setShowAddEdit(true);
    };

    const closeForm = () => {
        setShowAddEdit(false);
        setDraft({});
    };

    const validFields = (): boolean => {
        return fields.every((f) => !f.required || (draft[f.key] !== undefined && draft[f.key] !== null && draft[f.key] !== ''));
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }
        try {
            showBackDrop(true);
            const isEditing = draft.id !== undefined && draft.id !== null;
            await addUpdateFn(draft);
            showMessage({ type: 'success', message: isEditing ? 'Registro atualizado com sucesso' : 'Registro criado com sucesso' });
            closeForm();
            await loadData();
            onSaved?.();
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar registro' });
        } finally {
            showBackDrop(false);
        }
    };

    const filteredItems = appliedSearchTerm
        ? items.filter((row) => fields.some((f) => String(row[f.key] ?? '').toLowerCase().includes(appliedSearchTerm.toLowerCase())))
        : items;

    const columns: GridColDef[] = [
        ...fields.map((f) => ({ field: f.key, headerName: f.label, flex: 1, minWidth: 140 } as GridColDef)),
        {
            field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
            renderCell: (params) => (
                <Tooltip title="Editar">
                    <IconButton size="small" color="primary" onClick={() => openForm(params.row)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    const search = (
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={1.5}>
                <Grid item lg={8} sm={8} xl={8}>
                    <InputLabel>Buscar por</InputLabel>
                    <OutlinedInput
                        name="input-term"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setAppliedSearchTerm(searchTerm); }}
                        fullWidth
                    />
                </Grid>
                <Grid item lg={2} sm={2} xl={2} sx={{ mt: 1, display: 'flex', flexDirection: "column", justifyContent: "flex-end", alignItems: "flex-end" }}>
                    <Button variant="contained" fullWidth size="small" onClick={() => setAppliedSearchTerm(searchTerm)} startIcon={<SearchIcon />}>Consultar</Button>
                </Grid>
                <Grid item lg={2} sm={2} xl={2} sx={{ mt: 1, display: 'flex', flexDirection: "column", justifyContent: "flex-end", alignItems: "flex-end" }}>
                    <Button variant="outlined" fullWidth size="small" onClick={() => { setSearchTerm(''); setAppliedSearchTerm(''); }} startIcon={<ClearIcon />}>Limpar</Button>
                </Grid>
            </Grid>
        </Paper>
    );

    const grid = variant === 'standalone' ? (
        <Paper sx={{ p: 2, minHeight: "500px", display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <DataGrid
                rows={filteredItems}
                columns={columns}
                {...dataGridDefaultProps}
                paginationMode="client"
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                sx={dataGridDefaultSx}
                localeText={getDataGridLocaleText(filteredItems.length, 10)}
                loading={loading}
                getRowId={(row) => row.id}
                disableRowSelectionOnClick
                onRowClick={(params) => openForm(params.row)}
            />
            <SpeedDial
                ariaLabel={`Adicionar ${title}`}
                sx={{ position: 'absolute', bottom: 16, right: 16 }}
                onClick={() => openForm()}
                icon={<SpeedDialIcon />}
            >
            </SpeedDial>
        </Paper>
    ) : (
        <DataGrid
            autoHeight
            rows={items}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            onRowClick={(params) => openForm(params.row)}
        />
    );

    const content = (
        <React.Fragment>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">{title}</Typography>
                {variant === 'embedded' && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openForm()}>Novo</Button>
                )}
            </Box>
            {grid}
        </React.Fragment>
    );

    return (
        <React.Fragment>
            {variant === 'standalone' ? (
                <React.Fragment>
                    <Grid item xs={12} md={12} lg={12}>
                        {search}
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                        {content}
                    </Grid>
                </React.Fragment>
            ) : (
                <Box>
                    {content}
                </Box>
            )}
            <DefaultDrawer
                isOpen={showAddEdit}
                title={draft?.id !== undefined && draft?.id !== null ? `Editar ${title}` : `Adicionar ${title}`}
                onClose={closeForm}
                actions={() => (
                    <Button variant="contained" endIcon={<SaveIcon />} onClick={() => {
                        showDialog({
                            open: true,
                            message: "Deseja realmente salvar essa informação?",
                            onYesClick: () => {
                                (async () => { await save(); })();
                            }
                        } as DefaultDialogProps);
                    }}>
                        Salvar
                    </Button>
                )}
            >
                <Grid container spacing={2}>
                    {fields.map((f) => {
                        const value = draft[f.key];
                        const isEmpty = value === undefined || value === null || value === '';
                        const showError = !!f.required && isEmpty;
                        return (
                            <Grid item xs={12} sm={6} key={f.key}>
                                <InputLabel>{f.label}{f.required ? ' *' : ''}</InputLabel>
                                {f.type === 'select' ? (
                                    <Select
                                        size="small"
                                        fullWidth
                                        error={showError}
                                        value={value ?? ''}
                                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                                    >
                                        <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                                        {f.options?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                                    </Select>
                                ) : (
                                    <React.Fragment>
                                        <OutlinedInput
                                            size="small"
                                            fullWidth
                                            error={showError}
                                            type={f.type === 'number' ? 'number' : 'text'}
                                            value={value ?? ''}
                                            inputProps={f.maxLength ? { maxLength: f.maxLength } : undefined}
                                            onChange={(e) => setDraft({ ...draft, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value })}
                                        />
                                        {f.maxLength && f.type !== 'number' && (
                                            <Typography variant="caption" color="text.secondary">
                                                {String(value ?? '').length}/{f.maxLength} caracteres
                                            </Typography>
                                        )}
                                    </React.Fragment>
                                )}
                            </Grid>
                        );
                    })}
                </Grid>
            </DefaultDrawer>
        </React.Fragment>
    );
};
